import { createContext, useContext } from "react";
import type { PersonPreferences, PreferencesResult } from "../api/preferences";

/** The preferences a person has before they change any of them. Mirrors the server's defaults. */
export const defaultPreferences: PersonPreferences = { showProviderWarnings: true, projectionLookbackDays: 7, animatedBackground: true };

export interface PreferencesContextValue {
  /** The signed-in person's preferences, or the defaults before they have loaded or while signed out. */
  preferences: PersonPreferences;
  /**
   * Saves every preference. The change shows at once and is rolled back if the save fails, so the
   * returned result tells the caller whether it stuck.
   */
  save: (next: PersonPreferences) => Promise<PreferencesResult>;
}

export const PreferencesContext = createContext<PreferencesContextValue>({
  preferences: defaultPreferences,
  save: () => Promise.resolve({ kind: "error", message: "Preferences are not available here." }),
});

/**
 * Returns the signed-in person's preferences and a way to save them. Outside a `PreferencesProvider` it
 * returns the defaults and a save that reports failure, so a component renders correctly on its own.
 *
 * @returns The current preferences and the save function.
 */
export function usePreferences(): PreferencesContextValue {
  return useContext(PreferencesContext);
}
